import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ApiError } from "../../shared/api/client";
import { postReviewDebugLineSub, type ReviewDebugLineSubResponse } from "../../shared/api/reviewDebug";
import { postLineLogin } from "../../shared/api/session";
import { getEnv } from "../../shared/lib/env";
import {
  LiffLoginIncompleteError,
  LiffLoginRedirectError,
  getIdTokenSafe,
  getLiffRuntimeState,
  loginWithRedirect,
  rememberLiffLoginReturnTo
} from "../../shared/lib/liff";
import { clearFlowCounters, getFlowCounter, incrementFlowCounter } from "../../shared/session/flowStorage";
import { PageScaffold } from "../../shared/ui/PageScaffold";

type DebugStatus = "idle" | "loading" | "redirecting" | "ready" | "error" | "unavailable";

function formatDebugError(error: unknown): string {
  if (error instanceof ApiError) {
    const lines = [
      `HTTP ${error.status}`,
      error.code ? `code: ${error.code}` : null,
      error.message ? `message: ${error.message}` : null,
      error.debug?.name ? `debug.name: ${error.debug.name}` : null,
      error.debug?.message ? `debug.message: ${error.debug.message}` : null,
      error.debug?.source ? `debug.source: ${error.debug.source}` : null
    ].filter((line): line is string => Boolean(line));

    return lines.join("\n");
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "LINE debug failed";
}

function createStepError(step: string, error: unknown): Error {
  return new Error(`${step}\n${formatDebugError(error)}`);
}

export function ReviewDebugLineSubPage() {
  const env = getEnv();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const [status, setStatus] = useState<DebugStatus>("idle");
  const [result, setResult] = useState<ReviewDebugLineSubResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadDebugLineSub() {
      if (!env.reviewDebugEnabled) {
        setStatus("unavailable");
        return;
      }

      setStatus("loading");
      setErrorMessage(null);
      setCopied(false);

      try {
        const currentDebugUrl = `/t/${tenantSlug}/debug/line-sub`;
        const runtimeState = await getLiffRuntimeState(env.lineMiniAppLiffId);

        if (!runtimeState.isLoggedIn) {
          if (getFlowCounter("debugLineSub", "loginAttemptCount") >= 1) {
            throw new LiffLoginIncompleteError();
          }

          incrementFlowCounter("debugLineSub", "loginAttemptCount");
          rememberLiffLoginReturnTo(currentDebugUrl);
          await loginWithRedirect(env.lineMiniAppLiffId, currentDebugUrl);
          return;
        }

        const idToken = await getIdTokenSafe(env.lineMiniAppLiffId);

        if (!idToken) {
          if (getFlowCounter("debugLineSub", "loginAttemptCount") >= 1) {
            throw new LiffLoginIncompleteError();
          }

          incrementFlowCounter("debugLineSub", "loginAttemptCount");
          rememberLiffLoginReturnTo(currentDebugUrl);
          await loginWithRedirect(env.lineMiniAppLiffId, currentDebugUrl);
          return;
        }

        clearFlowCounters("debugLineSub");
        try {
          await postLineLogin({ tenantSlug, idToken });
        } catch (error) {
          throw createStepError("session/line-login failed", error);
        }

        let nextResult: ReviewDebugLineSubResponse;

        try {
          nextResult = await postReviewDebugLineSub({
            tenantSlug,
            idToken
          });
        } catch (error) {
          throw createStepError("review/debug/line-sub failed", error);
        }

        if (cancelled) {
          return;
        }

        setResult(nextResult);
        setStatus("ready");
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (error instanceof LiffLoginRedirectError) {
          setResult(null);
          setStatus("redirecting");
          setErrorMessage(null);
          return;
        }

        setResult(null);
        setStatus("error");
        setErrorMessage(formatDebugError(error));
      }
    }

    void loadDebugLineSub();

    return () => {
      cancelled = true;
    };
  }, [tenantSlug]);

  async function copyLineSub() {
    if (!result) {
      return;
    }

    await navigator.clipboard.writeText(result.lineSub);
    setCopied(true);
  }

  return (
    <PageScaffold
      title="Review Debug: LINE sub"
      description="審査用の確認画面です。Firestore から raw LINE sub は読まず、現在の LIFF ID token をその場で検証して表示します。"
    >
      {status === "unavailable" ? <p>この debug 画面は review/debug build でのみ利用できます。</p> : null}
      {status === "loading" || status === "idle" ? <p>LINE ID token を確認しています。</p> : null}
      {status === "redirecting" ? <p>LINEログインに移動しています。画面が切り替わらない場合は、このURLをLINEミニアプリ内から開き直してください。</p> : null}
      {status === "error" ? (
        <div style={{ display: "grid", gap: 8 }}>
          <p style={{ margin: 0 }}>取得に失敗しました。</p>
          {errorMessage ? (
            <pre
              style={{
                margin: 0,
                padding: 12,
                borderRadius: 8,
                background: "#f6f6f6",
                overflowX: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word"
              }}
            >
              {errorMessage}
            </pre>
          ) : null}
        </div>
      ) : null}

      {status === "ready" && result ? (
        <div style={{ display: "grid", gap: 12 }}>
          <dl style={{ display: "grid", gap: 8, margin: 0 }}>
            <div>
              <dt style={{ fontWeight: 700 }}>lineSub</dt>
              <dd style={{ margin: 0, wordBreak: "break-all" }}>{result.lineSub}</dd>
            </div>
            <div>
              <dt style={{ fontWeight: 700 }}>platformUserId</dt>
              <dd style={{ margin: 0, wordBreak: "break-all" }}>{result.platformUserId}</dd>
            </div>
            <div>
              <dt style={{ fontWeight: 700 }}>tenantSlug</dt>
              <dd style={{ margin: 0 }}>{result.tenantSlug}</dd>
            </div>
            <div>
              <dt style={{ fontWeight: 700 }}>roles</dt>
              <dd style={{ margin: 0 }}>{result.roles.join(", ")}</dd>
            </div>
          </dl>

          <button type="button" onClick={() => void copyLineSub()}>
            lineSub をコピー
          </button>
          {copied ? <p style={{ margin: 0 }}>コピーしました。</p> : null}
        </div>
      ) : null}
    </PageScaffold>
  );
}
